import { useState, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface SourceIntelPanelProps {
  title: string
  icon: string
  color: string  // tailwind color name: 'red', 'cyan', 'purple', 'orange', 'amber', 'blue', 'teal', 'green', 'indigo'
  itemCount?: number
  countLabel?: string
  description?: string
  defaultExpanded?: boolean
  badge?: string
  badgeVariant?: 'critical' | 'high' | 'medium' | 'info' | 'clear'
  children: ReactNode
}

const COLOR_MAP: Record<string, { border: string; bg: string; text: string; headerBg: string; badge: string }> = {
  red: { border: 'border-red-500/30', bg: 'bg-red-900/10', text: 'text-red-400', headerBg: 'bg-red-900/20', badge: 'bg-red-600' },
  cyan: { border: 'border-cyan-500/30', bg: 'bg-cyan-900/10', text: 'text-cyan-400', headerBg: 'bg-cyan-900/20', badge: 'bg-cyan-600' },
  purple: { border: 'border-purple-500/30', bg: 'bg-purple-900/10', text: 'text-purple-400', headerBg: 'bg-purple-900/20', badge: 'bg-purple-600' },
  orange: { border: 'border-orange-500/30', bg: 'bg-orange-900/10', text: 'text-orange-400', headerBg: 'bg-orange-900/20', badge: 'bg-orange-600' },
  amber: { border: 'border-amber-500/30', bg: 'bg-amber-900/10', text: 'text-amber-400', headerBg: 'bg-amber-900/20', badge: 'bg-amber-600' },
  blue: { border: 'border-blue-500/30', bg: 'bg-blue-900/10', text: 'text-blue-400', headerBg: 'bg-blue-900/20', badge: 'bg-blue-600' },
  teal: { border: 'border-teal-500/30', bg: 'bg-teal-900/10', text: 'text-teal-400', headerBg: 'bg-teal-900/20', badge: 'bg-teal-600' },
  green: { border: 'border-green-500/30', bg: 'bg-green-900/10', text: 'text-green-400', headerBg: 'bg-green-900/20', badge: 'bg-green-600' },
  indigo: { border: 'border-indigo-500/30', bg: 'bg-indigo-900/10', text: 'text-indigo-400', headerBg: 'bg-indigo-900/20', badge: 'bg-indigo-600' },
}

const BADGE_VARIANT_MAP: Record<string, string> = {
  critical: 'bg-red-600 text-white border-red-500',
  high: 'bg-orange-600 text-white border-orange-500',
  medium: 'bg-yellow-600 text-white border-yellow-500',
  info: 'bg-blue-600 text-white border-blue-500',
  clear: 'bg-green-600 text-white border-green-500',
}

export function SourceIntelPanel({
  title,
  icon,
  color,
  itemCount,
  countLabel,
  description,
  defaultExpanded = true,
  badge,
  badgeVariant = 'info',
  children,
}: SourceIntelPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const colors = COLOR_MAP[color] || COLOR_MAP.red

  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden bg-black/30`}>
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-4 ${colors.headerBg} hover:brightness-110 transition-all`}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{icon}</span>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className={`font-mono text-sm font-semibold ${colors.text} tracking-wider`}>
                {title}
              </h3>
              {itemCount !== undefined && itemCount > 0 && (
                <Badge className={`${colors.badge} text-white text-xs font-mono border-0`}>
                  {itemCount} {countLabel || (itemCount === 1 ? 'result' : 'results')}
                </Badge>
              )}
              {badge && (
                <Badge className={`${BADGE_VARIANT_MAP[badgeVariant]} text-xs font-mono border`}>
                  {badge}
                </Badge>
              )}
            </div>
            {description && (
              <p className="text-gray-400 text-xs mt-0.5">{description}</p>
            )}
          </div>
        </div>
        <div className={`${colors.text} transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}>
          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
      </button>

      {/* Content - collapsible */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-gray-800/50">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
